import { ClusterOutlined } from '@ant-design/icons';
import { type NodeProps } from '@xyflow/react';
import CreateMultiIfElseMenu from 'automations/components/Nodes/CreateMultiIfElseMenu';
import { NodeType } from 'automations/data/nodeTypes';
import { getElseNodeSelector } from 'automations/selectors/reactFlow';
import {
  addNodeWithPosition,
  incrementExecutionOrder,
} from 'automations/store/reactFlowSlice';
import { ConditionTypes } from 'automations/types/automationConfig';
import { type MultiIfElseNodeType, nodeSchemas } from 'automations/types/nodeTypes';
import { didNodeSyncToServer, generateNewNodePayload } from 'automations/utils/node';
import { getErrorList } from 'automations/utils/zod';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useAutomationDispatch } from '../useAutomationDispatch';
import { type ConditionNodeReturnProps } from './useConditionNodeConfig';

export default function useMultiIfElse(node: NodeProps<MultiIfElseNodeType>): ConditionNodeReturnProps {
  const dispatch = useAutomationDispatch();
  const elseNode = useSelector(getElseNodeSelector(node.id));

  const errorMessages = useMemo(() => getErrorList(nodeSchemas.MultiIfElseNodeData, node.data), [node.data]);
  const AdditionalUI = useMemo(() => <CreateMultiIfElseMenu node={node} />, [node]);
  const isNodeSynced = didNodeSyncToServer(node);

  // Multi If Else's Menu can only be opened only once, when it is being created & never again
  const canOpenMenu = !isNodeSynced;

  const handleAddIfNode = useCallback(() => {
    const executionOrderToAdd = elseNode?.data.executionOrder ?? 0;
    const newNode = generateNewNodePayload({
      nodeType: NodeType.condition,
      data: {
        nodeSubType: ConditionTypes.if,
        executionOrder: executionOrderToAdd,
        menuOpen: true,
      },
    });
    if (elseNode) {
      // Update execution order of else node
      dispatch(incrementExecutionOrder({
        nodeId: elseNode.id,
        count: 1,
      }));
    }
    dispatch(addNodeWithPosition({
      newNode,
      sourceNodeId: node.id,
    }));
  }, [dispatch, elseNode, node.id]);

  return {
    icon: ClusterOutlined,
    hasErrors: errorMessages.length > 0,
    errorMessages,
    targetHandle: {
      onClick: handleAddIfNode,
    },
    AdditionalUI,
    canOpenMenu,
  };
}
